import { defineStore } from "pinia";
import type { UserState } from "@/types/user.ts";

/** 登录/刷新接口返回的用户信息（只保留 store 实际使用的字段） */
export interface LoginUserInfo {
  id: number;
  username: string;
  nickname: string;
  /** 有效角色 Key（直接授予的 ∪ 用户组继承的）：仅用于展示与排障 */
  roles?: string[];
  /** 有效权限点集合：判定的唯一依据，由后端下发 */
  permissions?: string[];
}

/** useUserStore() 的返回类型，供只依赖 store 少量方法的工具函数标注参数 */
export type UserStoreRef = ReturnType<typeof useUserStore>;

export const useUserStore = defineStore("user", {
  state: (): UserState => ({
    userId: -1,
    username: "",
    nickname: "",
    roles: [],
    permissions: [],
  }),
  getters: {
    getUserId(): number {
      return this.userId;
    },
    getUsername(): string {
      return this.username;
    },
    getNickname(): string {
      return this.nickname;
    },
  },
  actions: {
    clear() {
      this.$patch({
        userId: -1,
        username: "",
        nickname: "",
        roles: [],
        permissions: [],
      });
    },
    loadResponse(response: LoginUserInfo) {
      // 两个字段都做 `?? []` 兜底：后端对空列表带 omitempty，未登录或权限点为空时
      // 字段会整个缺席；若直接赋 undefined，state 就不再是数组，
      // 后续 includes/every 会抛错而整页白屏。
      this.$patch({
        userId: response.id,
        username: response.username,
        nickname: response.nickname,
        roles: response.roles ?? [],
        permissions: response.permissions ?? [],
      });
    },
    setNickname(nickname: string) {
      this.nickname = nickname;
    },
    isLogin(): boolean {
      return this.userId > 0;
    },
    /**
     * 是否拥有某个权限点。
     *
     * 依据是后端下发的 permissions，而不是本地由角色推算——见 auth/index.ts 的说明。
     * 权限点缺失时返回 false（fail-closed）：后端改了授权而本地的 permissions 还是旧的，
     * 表现为「菜单暂时看不到」，等页面刷新（App.vue 挂载时自动 POST /user/login/refresh）
     * 即可恢复；反过来若取「缺失即放行」，则会先给用户看到无权进入的入口。
     */
    hasAuth(auths: string): boolean {
      return this.hasAllAuths([auths]);
    },
    hasAllAuths(auths: string[]): boolean {
      if (!auths || auths.length === 0) {
        return true;
      }
      return auths.every((auth) => this.permissions.includes(auth));
    },
  },
  persist: true,
});
